import { useCallback, useState } from 'react';

import type { ProjectSelection } from '../shared/types';

export type UseSelectProjects = {
  selectedProjects: ProjectSelection;
  isProjectSelected: (projectLocator: string) => boolean;
  areSomeProjectsSelected: boolean;
  areAllProjectsOnPageSelected: boolean;
  areAllProjectsSelected: boolean;
  isSelectAllDisabled: boolean;
  onToggleIsProjectSelected: (projectLocator: string) => void;
  onToggleSelectAllProjects: () => void;
  onSelectAllAcrossAllPages: () => void;
  onClearSelectAll: () => void;
};

/**
 * Handles the state for selecting projects, both individually or in bulk,
 * on all of the pages which display the projects (CreateReleaseGroup, EditRelease, CloneRelease).
 *
 * Stores the selected projects as a Set of locators.
 */
const useSelectProjects = (
  //  The locators for the projects on this page
  projectLocators: string[],
): UseSelectProjects => {
  const [selectedProjects, setSelectedProjects] = useState<ProjectSelection>(new Set());

  //  Helper for determining if a project is selected
  const isProjectSelected = useCallback((projectLocator: string) =>
    selectedProjects === 'all' || selectedProjects.has(projectLocator), [selectedProjects]);

  //  Toggles if a project is selected or not. This function does not require you to pass any isChecked state,
  //  because it will automatically determine if the project is already selected or not.
  const onToggleIsProjectSelected = useCallback((projectLocator: string) => {
    setSelectedProjects((prevSelectedProjects) => {
      //  When all projects are selected, we should deselect this project being selected,
      //  but retain the selected state for the other ones.
      if (prevSelectedProjects === 'all') {
        return new Set(projectLocators.filter(locator => locator !== projectLocator));
      }

      //  Create a new Set based on the previous Set. We need a new reference, otherwise React may not re-render.
      const nextSelectedProjects = new Set(prevSelectedProjects);
      //  Update based on if this locator is already in the Set.
      if (prevSelectedProjects.has(projectLocator)) {
        nextSelectedProjects.delete(projectLocator);
      } else {
        nextSelectedProjects.add(projectLocator);
      }
      //  Ensure we return the updated set to this hook's state.
      return nextSelectedProjects;
    });
  }, [projectLocators]);

  //  Note: this is technically part of CORE-3337 oh well.
  const onToggleSelectAllProjects = useCallback(() => {
    setSelectedProjects((prevSelectedProjects) => {
      if (prevSelectedProjects === 'all' || prevSelectedProjects.size > 0) {
        return new Set();
      }

      return new Set(projectLocators);
    });
  }, [projectLocators]);

  const onSelectAllAcrossAllPages = () => setSelectedProjects('all');
  const onClearSelectAll = useCallback(() => setSelectedProjects(new Set()), []);

  //  If the user filters things to a point where there are no projects on the page, we want the selection stuff to handle correctly.
  const hasSomeProjectsOnPage = projectLocators.length > 0;

  return {
    selectedProjects,
    isProjectSelected,
    //  This is used to make the checkbox into an "indeterminate" state when some projects are selected.
    areSomeProjectsSelected: hasSomeProjectsOnPage && (selectedProjects !== 'all' && selectedProjects.size > 0),
    areAllProjectsOnPageSelected: hasSomeProjectsOnPage && (selectedProjects === 'all' || selectedProjects.size === projectLocators.length),
    areAllProjectsSelected: hasSomeProjectsOnPage && selectedProjects === 'all',
    isSelectAllDisabled: !hasSomeProjectsOnPage,
    onToggleIsProjectSelected,
    onToggleSelectAllProjects,
    onSelectAllAcrossAllPages,
    onClearSelectAll,
  };
};

export default useSelectProjects;
